import React, { useEffect, useState } from "react";
import { CheckCircle2, Copy, Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";

import Card from "src/components/common/card/Card";
import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import { Badge } from "src/components/ui/Badge";
import { formatDateTime } from "src/utils/date-time";
import { GatewaySettings } from "src/types/salePayment.types";

interface GatewaySettingsCardProps {
  gateway: GatewaySettings;
  saving: boolean;
  disconnecting: boolean;
  onSave: (values: Record<string, string>) => Promise<boolean>;
  onDisconnect: () => void;
}

const emptyValues = (gateway: GatewaySettings) =>
  Object.fromEntries(
    gateway.fields.map((f) => [f.key, f.secret ? "" : gateway.values[f.key] || ""])
  );

const GatewaySettingsCard: React.FC<GatewaySettingsCardProps> = ({
  gateway,
  saving,
  disconnecting,
  onSave,
  onDisconnect,
}) => {
  // Secret inputs start empty: the saved secret never comes back from the
  // server, and leaving the field blank keeps it.
  const [values, setValues] = useState<Record<string, string>>(emptyValues(gateway));
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setValues(emptyValues(gateway));
    setRevealed({});
  }, [gateway]);

  const initial = emptyValues(gateway);
  const isDirty = gateway.fields.some((f) => (values[f.key] || "") !== (initial[f.key] || ""));
  // A first connection needs every field; an update only what changed.
  const isComplete = gateway.fields.every(
    (f) => !!values[f.key]?.trim() || (gateway.connected && f.secret)
  );

  const handleSave = async () => {
    const ok = await onSave(values);
    if (ok) setRevealed({});
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(gateway.webhookUrl);
      toast.success("Webhook URL copied");
    } catch {
      toast.error("Couldn't copy — select it and copy manually");
    }
  };

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {gateway.displayName}
          </h3>
          {gateway.connected && gateway.connectedAt && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Connected on {formatDateTime(gateway.connectedAt, "DD MMM YYYY")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {gateway.connected ? (
            <>
              <span className="flex items-center gap-1 text-sm font-medium text-green-700 dark:text-green-400">
                <CheckCircle2 size={15} />
                Connected
              </span>
              {gateway.mode && (
                <Badge
                  text={gateway.mode === "live" ? "Live" : "Test mode"}
                  color={gateway.mode === "live" ? "green" : "yellow"}
                />
              )}
            </>
          ) : (
            <Badge text="Not connected" color="gray" />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {gateway.fields.map((field) => {
          const savedMask = field.secret ? gateway.values[field.key] : undefined;
          return (
            <Input
              key={field.key}
              label={field.label}
              required={!gateway.connected}
              type={field.secret && !revealed[field.key] ? "password" : "text"}
              autoComplete="off"
              placeholder={
                savedMask ? `${savedMask} (saved — leave blank to keep)` : field.placeholder
              }
              value={values[field.key] || ""}
              onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
              helperText={field.helpText}
              endAdornment={
                field.secret ? (
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    title={revealed[field.key] ? "Hide" : "Show"}
                    onClick={() =>
                      setRevealed({ ...revealed, [field.key]: !revealed[field.key] })
                    }
                  >
                    {revealed[field.key] ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                ) : undefined
              }
            />
          );
        })}
      </div>

      <div className="mt-4 rounded-xl bg-gray-50 p-3 dark:bg-gray-900/50">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Webhook URL</div>
        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          In your {gateway.displayName} dashboard, add a webhook with this URL, pick the
          payment link events (paid, partially paid, expired, cancelled) and set the same
          secret as above. This is how payments show up here automatically.
        </p>
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
          <span className="min-w-0 flex-1 truncate font-mono text-xs text-gray-700 dark:text-gray-300">
            {gateway.webhookUrl}
          </span>
          <button
            type="button"
            onClick={copyWebhook}
            className="text-gray-500 hover:text-primary-600 dark:text-gray-400"
            title="Copy webhook URL"
          >
            <Copy size={15} />
          </button>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        {gateway.connected && (
          <Button
            variant="outline"
            color="danger"
            onClick={onDisconnect}
            isLoading={disconnecting}
            disabled={saving}
          >
            Disconnect
          </Button>
        )}
        <Button
          onClick={handleSave}
          isLoading={saving}
          disabled={!isDirty || !isComplete || saving || disconnecting}
        >
          {gateway.connected ? "Save" : "Save & connect"}
        </Button>
      </div>
    </Card>
  );
};

export default GatewaySettingsCard;
